'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import Button from '../ui/Button';
import ThemeSwitcher from '../ui/ThemeSwitcher';
import LanguageSelector from '../ui/LanguageSelector';

import { useTranslations } from '@/lib/i18n/TranslationProvider';

const CHROME_URL = "https://chromewebstore.google.com/detail/wa-contacts-extractor-wav/dembjmabcapfkbnbbcphbfcjoghlkcoi";

export default function Navbar() {
    const { t, locale } = useTranslations();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    const NAV_LINKS = [
        { name: t.nav.features, href: `/${locale === 'en' ? '' : locale}#features` },
        { name: t.nav.pricing, href: `/${locale === 'en' ? '' : locale}#pricing` },
        { name: t.nav.guides, href: `/${locale === 'en' ? '' : locale}guides` },
        { name: t.nav.privacy, href: `/${locale === 'en' ? '' : locale}privacy` },
        { name: t.nav.faq, href: `/${locale === 'en' ? '' : locale}#faq` },
    ];

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileOpen) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    return (
        <>
            <header
                className={`
                    fixed top-[var(--promo-height,0px)] left-0 right-0 z-[100] w-full h-20 flex items-center transition-all duration-500
                    ${scrolled 
                        ? 'refractive-glass kinetic-navbar-border translate-y-0 shadow-cinematic-2' 
                        : 'bg-[var(--color-md-surface)]/[0.85] backdrop-blur-[16px] border-b border-[var(--color-md-outline)]/[0.1]'}
                `}
            >
                <div className="container mx-auto px-6 h-full flex items-center justify-between gap-8 py-2">
                    {/* Left: Logo - Technical Precision */}
                    <Link href="/" className="flex items-center gap-3 group shrink-0">
                        <div className="relative w-9 h-9 rounded-xl overflow-hidden shadow-sm group-hover:shadow-cinematic-2 transition-transform group-hover:scale-105 border border-[var(--color-md-outline)]/20">
                            <Image src="/icon.png" alt="WaVault" width={36} height={36} className="object-cover" />
                        </div>
                        <span className="font-display font-black tracking-[-0.03em] text-[var(--color-md-on-surface)] group-hover:text-[var(--color-md-primary)] transition-colors hidden sm:block text-lg">
                            WaVault
                        </span>
                    </Link>

                    {/* Center: Navigation Links - Luminous Typography */}
                    <nav className="hidden md:flex flex-1 justify-center items-center gap-2 relative h-full">
                        {NAV_LINKS.map((link) => (
                            <NavLink key={link.name} link={link} />
                        ))}
                    </nav>

                    {/* Right: Actions - High Contrast */}
                    <div className="flex items-center gap-4 shrink-0">
                        <div className="hidden lg:flex items-center gap-2">
                            <LanguageSelector />
                            <ThemeSwitcher />
                        </div>
                        <div className="hidden lg:block w-[1px] h-4 bg-[var(--color-md-outline)]/20 mx-2" />
                        <Button
                            href={CHROME_URL}
                            target="_blank"
                            variant="primary"
                            className="hidden md:inline-flex px-8 h-12 text-xs font-black tracking-tight btn-glow rounded-full transition-all hover:scale-[1.02]"
                        >
                            {t.nav.addToChrome}
                        </Button>
                        <button
                            type="button"
                            onClick={() => setMobileOpen(true)}
                            className="md:hidden p-3 rounded-xl bg-[var(--color-md-surface-variant)] text-[var(--color-md-on-surface)] hover:bg-[var(--color-md-primary)] hover:text-white transition-colors"
                            aria-label="Open menu"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile drawer */}
            <AnimatePresence>
                {mobileOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-md md:hidden"
                            onClick={() => setMobileOpen(false)}
                            aria-hidden="true"
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                            className="fixed top-0 right-0 bottom-0 z-[120] w-full max-w-[320px] bg-[var(--color-md-surface)] border-l border-[var(--color-md-outline)]/20 shadow-cinematic-4 md:hidden flex flex-col"
                        >
                            <div className="flex items-center justify-between p-6 border-b border-[var(--color-md-outline)]/10">
                                <span className="font-display font-black uppercase tracking-widest text-xs text-[var(--color-md-on-surface)]">{t.nav.navigation}</span>
                                <button
                                    type="button"
                                    onClick={() => setMobileOpen(false)}
                                    className="p-2 rounded-lg hover:bg-[var(--color-md-surface-variant)] transition-colors"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <nav className="flex-1 p-6 flex flex-col gap-1">
                                {NAV_LINKS.map((link) => (
                                    <Link
                                        key={link.name}
                                        href={link.href}
                                        onClick={() => setMobileOpen(false)}
                                        className="py-4 px-4 rounded-xl text-[var(--color-md-on-surface)] font-bold hover:bg-[var(--color-md-surface-variant)] hover:text-[var(--color-md-primary)] transition-all uppercase tracking-widest text-xs"
                                    >
                                        {link.name}
                                    </Link>
                                ))}
                            </nav>
                            <div className="p-8 border-t border-[var(--color-md-outline)]/10 bg-[var(--color-md-surface-variant)]/30">
                                <div className="flex items-center justify-between mb-8">
                                    <LanguageSelector />
                                    <ThemeSwitcher />
                                </div>
                                <Button
                                    size="lg"
                                    className="px-14 h-16 text-lg font-black btn-glow rounded-full shadow-cinematic-3 hover:shadow-cinematic-4"
                                    href={CHROME_URL}
                                    target="_blank"
                                >
                                    {t.nav.addToChrome} — It&apos;s Free
                                </Button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}

function NavLink({ link }: { link: { name: string; href: string } }) {
    const [isHovered, setIsHovered] = useState(false);
    return (
        <Link
            href={link.href}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="px-6 py-2 text-xs font-black uppercase tracking-[0.15em] text-[var(--color-md-on-surface-variant)] hover:text-[var(--color-md-on-surface)] transition-all duration-300 relative group"
        >
            <span className="relative z-10">{link.name}</span>
            {isHovered && (
                <motion.span
                    layoutId="nav-hover-pill"
                    className="absolute inset-0 bg-[var(--color-md-primary)]/[0.08] rounded-full z-0 blur-[4px] border border-[var(--color-md-primary)]/20"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 450, damping: 30 }}
                />
            )}
        </Link>
    );
}
